<template>
  <div class="score-management">
    <el-card class="score-exam-card" style="margin-bottom: 20px;">
      <div slot="header" class="card-header">
        <span>成绩管理</span>
      </div>

      <!-- 搜索区域 -->
      <div class="filter-container">
        <el-row :gutter="20">
          <el-col :span="5">
            <el-input v-model="searchForm.studentId" placeholder="输入学生ID"></el-input>
          </el-col>
          <el-col :span="5">
            <el-input v-model="searchForm.studentName" placeholder="输入学生姓名"></el-input>
          </el-col>
          <el-col :span="9">
            <el-button type="primary" @click="handleSearch">查询</el-button>
            <el-button @click="handleReset">重置</el-button>
          </el-col>
        </el-row>
      </div>
    
    <div class="table-container">
      <el-table
        v-loading="loading"
        :data="filteredData"
        style="width: 100%"
        border
        highlight-current-row
      >
        <el-table-column prop="id" label="成绩id" width="120" align="center" />
        <el-table-column prop="studentId" label="学生id" width="120" align="center" />
        <el-table-column prop="studentName" label="学生姓名" width="140" align="center" />
        <el-table-column prop="projectId" label="项目id" width="110" align="center" />
        <el-table-column prop="activityId" label="活动id" width="110" align="center" />
        <el-table-column prop="actualValue" label="实测数值" width="150" align="center" />
        <el-table-column prop="grade" label="成绩等级" width="120" align="center">
          <template #default="scope">
            <el-tag
              :type="getGradeType(scope.row.grade)"
              size="small"
              effect="plain"
            >
              {{ scope.row.grade }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="isMakeup" label="是否补考" width="110" align="center">
          <template #default="scope">
            {{ scope.row.isMakeup ? '是' : '否' }}
          </template>
        </el-table-column>
        <el-table-column prop="approvalTeacher" label="审批教师" width="130" align="center" />
        <el-table-column prop="projectScore" label="项目成绩" width="130" align="center" />
        <el-table-column label="操作" width="160" fixed="right" align="center">
          <template #default="scope">
            <el-button type="text" @click="handleEdit(scope.row)">
              编辑
            </el-button>
            <el-button type="text" danger @click="handleDelete(scope.row)">
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>
    
    <!-- 分页组件 -->
    <div class="pagination-container">
      <el-pagination
        v-model:current-page="currentPage"
        v-model:page-size="pageSize"
        :page-sizes="[10, 20, 50, 100]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="scoreList.length"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>
    
    <!-- 编辑对话框 -->
    <el-dialog
      v-model="dialogVisible"
      title="编辑成绩详情"
      width="600px"
      destroy-on-close
    >
      <el-form :model="dialogData" label-width="120px" size="large">
        <el-form-item label="成绩id" disabled>
          <el-input v-model="dialogData.id" disabled />
        </el-form-item>
        <el-form-item label="学生id" disabled>
          <el-input v-model="dialogData.studentId" disabled />
        </el-form-item>
        <el-form-item label="学生姓名" disabled>
          <el-input v-model="dialogData.studentName" disabled />
        </el-form-item>
        <el-form-item label="项目id" disabled>
          <el-input v-model="dialogData.projectId" disabled />
        </el-form-item>
        <el-form-item label="活动id" disabled>
          <el-input v-model="dialogData.activityId" disabled />
        </el-form-item>
        <el-form-item label="实测数值" required>
          <el-input v-model="dialogData.actualValue" placeholder="请输入实测数值" />
        </el-form-item>
        <el-form-item label="成绩等级" required>
          <el-select v-model="dialogData.grade" placeholder="请选择成绩等级">
            <el-option label="优秀" value="优秀" />
            <el-option label="良好" value="良好" />
            <el-option label="及格" value="及格" />
            <el-option label="不及格" value="不及格" />
          </el-select>
        </el-form-item>
        <el-form-item label="是否补考">
          <el-switch v-model="dialogData.isMakeup" active-text="是" inactive-text="否" />
        </el-form-item>
        <el-form-item label="审批教师" required>
          <el-input v-model="dialogData.approvalTeacher" placeholder="请输入审批教师" />
        </el-form-item>
        <el-form-item label="项目成绩" required>
          <el-input v-model.number="dialogData.projectScore" placeholder="请输入项目成绩" type="number" />
        </el-form-item>
        <el-form-item label="测试日期">
          <el-input v-model="dialogData.testDate" disabled />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSave">提交</el-button>
      </template>
    </el-dialog>
  </el-card>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import request from '@/utils/request';

// 响应式数据
const loading = ref(false);
const currentPage = ref(1);
const pageSize = ref(10);
const dialogVisible = ref(false);
const dialogData = ref({});
const searchForm = ref({
  studentId: '',
  studentName: ''
});

// 成绩数据 - 将通过API获取
const scoreList = ref([]);

// 根据成绩等级返回对应的标签类型
const getGradeType = (grade) => {
  switch (grade) {
    case '优秀':
      return 'success';
    case '良好':
      return 'primary';
    case '及格':
      return 'warning';
    case '不及格':
      return 'danger';
    default:
      return 'info';
  }
};

// 保存搜索条件的响应式变量，只有在点击查询按钮时才更新
const activeSearchForm = ref({});

// 计算属性 - 过滤后的数据
const filteredData = computed(() => {
  let filtered = scoreList.value;
  
  // 根据当前活跃的搜索条件过滤
  if (activeSearchForm.value.studentId) {
    filtered = filtered.filter(item => 
      String(item.studentId).toLowerCase().includes(activeSearchForm.value.studentId.toLowerCase())
    );
  }
  
  if (activeSearchForm.value.studentName) {
    filtered = filtered.filter(item => 
      item.studentName.toLowerCase().includes(activeSearchForm.value.studentName.toLowerCase())
    );
  }
  
  // 分页计算
  const start = (currentPage.value - 1) * pageSize.value;
  const end = start + pageSize.value;
  return filtered.slice(start, end);
});

// 格式化日期函数（暂时保留，如需使用可取消注释）
/*
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleString('zh-CN');
};
*/

// 搜索处理
const handleSearch = () => {
  currentPage.value = 1; // 重置到第一页
  // 更新活跃的搜索条件，触发表格数据过滤
  activeSearchForm.value = { ...searchForm.value };
  fetchScores();
};

// 重置搜索
const handleReset = () => {
  searchForm.value = {
    studentId: '',
    studentName: ''
  };
  activeSearchForm.value = {}; // 清除活跃的搜索条件
  currentPage.value = 1;
  fetchScores();
};

// 编辑详情
const handleEdit = (row) => {
  dialogData.value = { ...row };
  dialogVisible.value = true;
};

// 保存编辑
const handleSave = async () => {
  try {
    // 调用管理员专用API保存修改
    // 准备请求数据，只包含需要更新的字段
    const updateData = {
      projectScore: dialogData.value.projectScore,
      isMakeupExam: dialogData.value.isMakeup ? '是' : '否',
      measuredValue: dialogData.value.actualValue,
      gradeLevel: dialogData.value.grade,
      reviewingTeacherId: dialogData.value.approvalTeacher
    };
   // 调用管理员专用API更新
    const response = await request.put(`/admin/scores/${dialogData.value.id}`, updateData);
    
    if (response.code === 200) {
      // 重新获取数据以保持同步
      fetchScores();
      ElMessage.success('提交成功');
      dialogVisible.value = false;
    } else {
      ElMessage.error(response.message || '提交失败');
    }
  } catch (error) {
    ElMessage.error('提交失败');
    console.error('Failed to save score:', error);
  }
};

// 删除记录
const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm(
      `确定要删除成绩记录 ID: ${row.id} 吗？`,
      '警告',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }
    );
    
    // 调用管理员专用API删除
    const response = await request.delete(`/admin/scores/${row.id}`);
    
    if (response.code === 200) {
      // 从本地数组中移除数据
      const index = scoreList.value.findIndex(item => item.id === row.id);
      if (index > -1) {
        scoreList.value.splice(index, 1);
      }
      ElMessage.success('删除成功');
    } else {
      ElMessage.error(response.message || '删除失败');
    }
  } catch (error) {
    // 用户取消删除或API调用失败
    if (error !== 'cancel') {
      ElMessage.error('删除失败');
      console.error('Failed to delete score:', error);
    }
  }
};

// 分页处理
const handleSizeChange = (size) => {
  pageSize.value = size;
};

const handleCurrentChange = (current) => {
  currentPage.value = current;
};

// 从API获取成绩数据的函数
const fetchScores = async () => {
  try {
    loading.value = true;
    // 清空数据，防止显示旧数据
    scoreList.value = [];
    
    // 调用管理员专用API获取数据
    const response = await request.get('/admin/scores/', searchForm.value);
    
    console.log('API响应数据:', response);
    
    // 准备要处理的数据
    let dataToProcess = [];
    
    // 检查响应数据结构并提取数据
    if (response && response.code === 200 && response.data) {
      dataToProcess = Array.isArray(response.data) ? response.data : [response.data];
    } else if (response && response.code === 200 && Array.isArray(response)) {
      dataToProcess = response;
    } else if (response && response.code === 200) {
      // 其他成功情况，但没有数据
      dataToProcess = [];
    } else {
      console.log('API返回格式不符合预期:', response);
      ElMessage.error(response?.message || '获取成绩数据失败');
      return;
    }
    
    // 转换数据格式以匹配前端组件需要
    if (dataToProcess.length > 0) {
      scoreList.value = dataToProcess.map(item => ({
        id: item.gradeId,
        studentId: item.studentId,
        studentName: item.studentName || '未知',
        projectId: item.itemId,
        activityId: item.activityId,
        actualValue: item.measuredValue || '',
        grade: item.gradeLevel || '未评级',
        isMakeup: item.isMakeupExam === '是' || item.isMakeupExam === '1' || item.isMakeupExam === true,
        approvalTeacher: item.reviewingTeacherId || '',
        projectScore: item.projectScore || 0,
        testDate: item.testDate || ''
      }));
    }
    
    console.log('转换后的数据:', scoreList.value);
    console.log('数据数量:', scoreList.value.length);
    
    // 如果没有数据，显示提示信息
    if (scoreList.value.length === 0) {
      ElMessage.info('未查询到符合条件的成绩记录');
    }
  } catch (error) {
    ElMessage.error('获取成绩数据失败');
    console.error('Failed to fetch scores:', error);
  } finally {
    loading.value = false;
  }
};

// 组件挂载时从API获取数据
onMounted(() => {
  activeSearchForm.value = {}; // 确保初始时没有活跃的搜索条件
  fetchScores();
});
</script>

<style scoped>
.score-management {
  padding: 10px 0;
}

.card-header {
  font-size: 16px;
  color: #303133;
}

.filter-container {
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.table-container {
  margin-bottom: 20px;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  padding: 10px 0;
}
</style>